import type { Account } from "@/types";
import { computed, type ComputedRef } from "vue";

/**
 * SECURITY: Narrow admin-session detection (staff role OR an admin-driven
 * session, i.e. admin_account_id set). Use this for safety-critical gates (e.g.
 * hiding the clickwrap consent checkbox) that must stay in sync with the
 * server's authoritative decision.
 *
 * Intentionally NOT the same as `isAdminAccount`, which also treats regular
 * `internal_role === "admin"` users as admin. The backend's consent-suppression
 * only recognises super_admin / impersonation; using the broader check would
 * hide the checkbox for regular admins while the backend still required
 * `agreed_to_terms=true`, breaking signup for them.
 *
 * Pass a getter that returns the current account so the caller can plug in
 * either a `ref<Account | null>` (`() => account.value`) or a `reactive<Account>`
 * (`() => accountData`) without the composable caring which.
 */
export function useAdminSession(
  getAccount: () => Account | null | undefined
): ComputedRef<boolean> {
  return computed(() => {
    const a = getAccount();
    if (!a) return false;
    return (
      !!a.admin_account_id ||
      a.internal_role === "super_admin" ||
      a.internal_role === "sales_admin"
    );
  });
}
